interface FetchParams {
    url: string;
    options: RequestInit;
}

type RequestInterceptor = (params: FetchParams) => FetchParams | Promise<FetchParams>
type ResponseInterceptor = (params: Response) => Response | Promise<Response> | never

export class ApiRequestInterceptors {
    private static interceptorsMap = new Map<string, RequestInterceptor>()
    static set(name: string, interceptor: RequestInterceptor) {
        ApiRequestInterceptors.interceptorsMap.set(name, interceptor)
    }
    static delete(name: string) {
        ApiRequestInterceptors.interceptorsMap.delete(name)
    }
    static has(name: string) {
        return ApiRequestInterceptors.interceptorsMap.has(name)
    }

    static [Symbol.iterator]() {
        return ApiRequestInterceptors.interceptorsMap.values()
    }
}

export class ApiResponseInterceptors {
    private static interceptorsMap = new Map<string, ResponseInterceptor>()
    static set(name: string, interceptor: ResponseInterceptor) {
        ApiResponseInterceptors.interceptorsMap.set(name, interceptor)
    }
    static delete(name: string) {
        ApiResponseInterceptors.interceptorsMap.delete(name)
    }
    static has(name: string) {
        return ApiResponseInterceptors.interceptorsMap.has(name)
    }

    static [Symbol.iterator]() {
        return ApiResponseInterceptors.interceptorsMap.values()
    }
}

class Api {
    constructor () {
        ApiResponseInterceptors.set('default-throw-error', async (response) => {
            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(errorText);
            }
            return response
        })
    }

    private async request(params: FetchParams) {
        try {

            for (const interceptor of ApiRequestInterceptors) {
                if (params.options?.signal?.aborted) throw new Error(params.options?.signal.reason || 'Aborted')
                params = await interceptor(params)
            }

            let response = await fetch(params.url, params.options)

            for (const interceptor of ApiResponseInterceptors) {
                if (params.options?.signal?.aborted) throw new Error(params.options?.signal.reason || 'Aborted')
                response = await interceptor(response)
            }

            return await response.json();
        } catch (error) {
            console.error('Fetch error:', error);
            throw error;
        }
    }

    public get(url: string, signal?: AbortSignal) {
        return this.request({ url,  options: { method: 'GET', signal }});
    }

    public post(url: string, body?: BodyInit | null, signal?: AbortSignal) {
        return this.request({ url, options: { method: 'POST', body, signal }});
    }

    public put(url: string, body?: BodyInit | null, signal?: AbortSignal) {
        return this.request({ url, options: { method: 'PUT', body, signal }});
    }

    public delete(url: string, signal?: AbortSignal) {
        return this.request({ url, options: { method: 'DELETE', signal }});
    }
}

export default new Api();
